import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  IcSearch,
  IcClose,
  IcPlus,
  IcPencil,
  IcTrash,
  IcClock,
  IcTag,
  IcSparkles,
  IcCpu,
  IcFlower,
  IcGem,
} from '../../theme/icons';
import Swal from '../../theme/swal';
import serviceManagementService from '../../services/serviceManagementService';
import authService from '../../services/authService';
import KI from '../../theme/palette';
import { matchesSearch } from '../../utils/search';

// Type de service -> French label, badge variant (Étiquettes §8) and icon.
const TYPE_META = {
  DIAGNOSTIC: { label: 'Diagnostique', className: 'bdg-type-diag', Icon: IcCpu },
  SOIN: { label: 'Soin', className: 'bdg-type-soin', Icon: IcFlower },
  ACTE: { label: 'Acte', className: 'bdg-type-acte', Icon: IcGem },
};

// Human-readable minutes -> "15 min" / "2 h" / "1 h 30 min".
const formatDuration = (mins) => {
  const m = parseInt(mins, 10);
  if (!m || m <= 0) return null;
  const h = Math.floor(m / 60);
  const r = m % 60;
  if (h && r) return `${h} h ${r} min`;
  if (h) return `${h} h`;
  return `${m} min`;
};

const formatPrice = (value) =>
  typeof value === 'number' ? value.toFixed(3) : (parseFloat(value) || 0).toFixed(3);

function ServiceCard({ service, isAdmin, onEdit, onDelete }) {
  const type = TYPE_META[service.type];
  const TypeIcon = type ? type.Icon : IcSparkles;
  const hasRemise = service.remise > 0;
  const finalPrice = service.final_price != null
    ? service.final_price
    : service.price * (1 - (service.remise || 0) / 100);
  const duration = formatDuration(service.duration);

  return (
    <div className="flex items-center gap-3 p-3 bg-white rounded-2xl border border-gray-200">
      <div className="h-14 w-14 rounded-xl bg-gray-100 text-gray-500 flex-shrink-0 flex items-center justify-center overflow-hidden">
        {service.image ? (
          <img src={service.image} alt={service.name} className="h-full w-full object-cover" />
        ) : (
          <TypeIcon />
        )}
      </div>

      <div className="flex-1 min-w-0">
        <div className="font-medium text-gray-900 truncate">{service.name}</div>
        <div className="mt-1 flex flex-wrap items-center gap-1.5">
          {/* Étiquettes §13 : ● Actif vert teinté · ○ Inactif gris neutre. */}
          <span className={`bdg ${service.is_active ? 'bdg-pos' : 'bdg-neutral'} py-0.5 rounded-full`}>
            {service.is_active ? '● Actif' : '○ Inactif'}
          </span>
          {type && (
            <span className={`bdg ${type.className} py-0.5 rounded-full`}>
              <type.Icon className="text-[10px]" /> {type.label}
            </span>
          )}
          {duration && (
            <span className="inline-flex items-center gap-1 text-xs text-gray-400">
              <IcClock /> {duration}
            </span>
          )}
        </div>
        <div className="mt-1 flex flex-wrap items-center gap-2">
          <span className="text-sm font-semibold text-gray-800">
            {formatPrice(finalPrice)}
            <span className="text-[11px] text-gray-500 ml-0.5">DT</span>
          </span>
          {hasRemise && (
            <>
              <span className="text-xs text-gray-500 line-through">{formatPrice(service.price)} DT</span>
              {/* Promo −N% (Étiquettes §12) : amber plein. */}
              <span className="bdg bdg-promo px-1.5 py-0.5 rounded-full">
                <IcTag /> −{service.remise}%
              </span>
            </>
          )}
        </div>
      </div>

      {isAdmin && (
        <div className="flex flex-col gap-2 flex-shrink-0">
          <button
            onClick={() => onEdit(service.id)}
            aria-label="Modifier"
            className="h-9 w-9 flex items-center justify-center rounded-xl bg-gray-100 text-gray-500 hover:bg-gray-200 transition-colors"
          >
            <IcPencil />
          </button>
          <button
            onClick={() => onDelete(service)}
            aria-label="Supprimer"
            className="h-9 w-9 flex items-center justify-center rounded-xl text-red-600 hover:bg-red-50 transition-colors"
          >
            <IcTrash />
          </button>
        </div>
      )}
    </div>
  );
}

export default function MobileServicesList() {
  const navigate = useNavigate();
  const [services, setServices] = useState([]);
  const [search, setSearch] = useState('');
  const [loading, setLoading] = useState(true);

  // Only admin can add/edit/delete services.
  const isAdmin = authService.isAdmin();

  useEffect(() => {
    const fetchServices = async () => {
      try {
        setLoading(true);
        const response = await serviceManagementService.getAllServices();
        const servicesList = Array.isArray(response?.results) ? response.results : (Array.isArray(response) ? response : []);
        setServices(servicesList);
      } catch (err) {
        console.error('Error fetching services:', err);
      } finally {
        setLoading(false);
      }
    };

    fetchServices();
  }, []);

  const handleEdit = (id) => navigate(`/services/edit/${id}`);

  const handleDelete = (service) => {
    Swal.fire({
      title: 'Êtes-vous sûr?',
      text: 'Cette action ne peut pas être annulée!',
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: KI.coralDark,
      cancelButtonColor: KI.faint,
      confirmButtonText: 'Oui, supprimer!',
      cancelButtonText: 'Annuler',
    }).then(async (result) => {
      if (!result.isConfirmed) return;
      try {
        await serviceManagementService.deleteService(service.id);
        setServices((prev) => prev.filter((s) => s.id !== service.id));
        Swal.fire('Supprimé!', 'Le service a été supprimé avec succès.', 'success');
      } catch (error) {
        console.error('Error deleting service:', error);
        let errorMessage = 'Une erreur est survenue lors de la suppression.';
        if (error.response && error.response.data && error.response.data.error) {
          errorMessage = error.response.data.error;
        } else if (error.message) {
          errorMessage = error.message;
        }
        Swal.fire('Erreur!', errorMessage, 'error');
      }
    });
  };

  // Client-side accent-insensitive filter.
  // Règle Ki Para (utils/search.js) : début de mot, sans accents.
  const displayedServices = services.filter((service) =>
    matchesSearch(search, [service.name, service.description, TYPE_META[service.type]?.label]));

  return (
    <div className="w-full px-4 py-6">
      {/* Nom de page = chip blanc bordure noire, comme MobileProductList
          (Studio 2026-08-14) ; ancien h1 blanc retiré. */}
      <div className="mb-4">
        <span className="inline-flex items-center gap-2 px-4 py-2 rounded-xl text-sm font-bold bg-white text-gray-900">
          <IcSparkles size={13} /> Services
        </span>
      </div>
      {/* Search */}
      <div className="relative mb-4">
        <IcSearch className="absolute left-3.5 top-1/2 -translate-y-1/2 text-gray-400" />
        <input
          type="text"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Rechercher par nom, description ou type..."
          className="w-full pl-11 pr-10 py-2.5 border border-gray-200 rounded-xl bg-white focus:outline-none focus:border-gray-900 focus:ring-1 focus:ring-gray-900"
        />
        {search && (
          <button
            onClick={() => setSearch('')}
            className="absolute right-3.5 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
          >
            <IcClose />
          </button>
        )}
      </div>

      {loading ? (
        <div className="flex justify-center items-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600" />
        </div>
      ) : displayedServices.length === 0 ? (
        <div className="bg-white rounded-2xl border border-gray-100 p-10 text-center">
          <IcSparkles className="mx-auto text-5xl text-gray-300 mb-4" />
          <p className="text-gray-500 font-medium">Aucun service trouvé</p>
        </div>
      ) : (
        <div className="space-y-2.5">
          {displayedServices.map((service) => (
            <ServiceCard
              key={service.id}
              service={service}
              isAdmin={isAdmin}
              onEdit={handleEdit}
              onDelete={handleDelete}
            />
          ))}
        </div>
      )}

      {isAdmin && (
        <button
          onClick={() => navigate('/services/add')}
          aria-label="Ajouter un service"
          className="btn-p fixed right-4 bottom-24 z-30 h-14 w-14 rounded-full shadow-lg transition-colors"
        >
          <IcPlus className="text-xl" />
        </button>
      )}
    </div>
  );
}
